import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { Button } from './ui/button';
import { Menu, X, Plus, LogOut, Home, Search, LayoutDashboard, HelpCircle, MessageCircle, Bell, Shield, Heart, Briefcase } from 'lucide-react';

export function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const { unreadCount, supportUnreadCount, notifUnreadCount, isAdmin, notificationPermission, requestNotificationPermission } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleEnableNotifications = async () => {
    await requestNotificationPermission();
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const NavLink = ({ to, children, icon: Icon, badge }) => (
    <Link
      to={to}
      onClick={() => setMobileMenuOpen(false)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 relative ${
        isActive(to) 
          ? 'text-primary bg-primary/10' 
          : 'text-gray-600 hover:text-primary hover:bg-primary/5'
      }`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {children}
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-[#E53935] text-white text-[10px] font-bold rounded-full px-1 ring-2 ring-white" data-testid="unread-badge">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/80 header-blur border-b border-gray-100" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <img src="/logo.png" alt="HabeshaTown" className="h-9 w-9 object-contain" />
            <span className="font-bold text-xl tracking-tight text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Habesha<span className="text-primary">Town</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            <NavLink to="/" icon={Home}>Home</NavLink>
            <NavLink to="/browse" icon={Search}>Browse</NavLink>
            <NavLink to="/jobs" icon={Briefcase}>Jobs</NavLink>
            {isAuthenticated && (
              <>
                <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
                {isAdmin ? (
                  <NavLink to="/support" icon={Shield} badge={supportUnreadCount}>Support</NavLink>
                ) : (
                  <NavLink to="/support" icon={HelpCircle}>Help</NavLink>
                )}
              </>
            )}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-2">
            {isAuthenticated ? (
              <>
                {/* Notification bell */}
                {notificationPermission === 'default' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleEnableNotifications}
                    className="relative text-gray-400 hover:text-gray-600"
                    title="Enable notifications"
                    data-testid="enable-notifications-btn"
                  >
                    <Bell className="h-4 w-4" />
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#E53935] rounded-full" />
                  </Button>
                )}

                {/* Notifications */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/notifications')}
                  className="relative text-gray-500 hover:text-gray-700"
                  data-testid="notifications-btn"
                >
                  <Bell className="h-4 w-4" />
                  {notifUnreadCount > 0 && (
                    <span 
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-[#E53935] text-white text-[10px] font-bold rounded-full px-1 ring-2 ring-white"
                      data-testid="notif-unread-badge"
                    >
                      {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                    </span>
                  )}
                </Button>

                {/* Favourites */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/favourites')}
                  className="relative text-gray-500 hover:text-gray-700"
                  data-testid="favourites-btn"
                >
                  <Heart className="h-4 w-4" />
                </Button>

                {/* Messages with unread badge */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/dashboard', { state: { tab: 'messages' } })}
                  className="relative text-gray-500 hover:text-gray-700"
                  data-testid="messages-btn"
                >
                  <MessageCircle className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span 
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-[#E53935] text-white text-[10px] font-bold rounded-full px-1 ring-2 ring-white"
                      data-testid="header-unread-badge"
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </Button>

                <Button 
                  size="sm" 
                  onClick={() => navigate('/create-listing')}
                  className="gap-2 bg-primary hover:bg-[#097549] text-white font-semibold"
                  data-testid="create-listing-btn"
                >
                  <Plus className="h-4 w-4" />
                  Post Listing
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleLogout}
                  className="gap-2 text-gray-500 hover:text-gray-700"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate('/login')}
                  className="text-gray-600 hover:text-gray-900 font-medium"
                  data-testid="login-btn"
                >
                  Login
                </Button>
                <Button 
                  size="sm"
                  onClick={() => navigate('/register')}
                  className="bg-primary hover:bg-[#097549] text-white font-semibold"
                  data-testid="register-btn"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>

          {/* Mobile: unread badge + menu button */}
          <div className="md:hidden flex items-center gap-1">
            {isAuthenticated && unreadCount > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { navigate('/dashboard', { state: { tab: 'messages' } }); setMobileMenuOpen(false); }}
                className="relative"
                data-testid="mobile-messages-btn"
              >
                <MessageCircle className="h-5 w-5 text-gray-600" />
                <span 
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-[#E53935] text-white text-[10px] font-bold rounded-full px-1 ring-2 ring-white"
                  data-testid="mobile-unread-badge"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </Button>
            )}
            <button
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6 text-gray-700" /> : <Menu className="h-6 w-6 text-gray-700" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white" data-testid="mobile-menu">
          <nav className="flex flex-col p-4 gap-2">
            <NavLink to="/" icon={Home}>Home</NavLink>
            <NavLink to="/browse" icon={Search}>Browse</NavLink>
            <NavLink to="/jobs" icon={Briefcase}>Jobs</NavLink>
            {isAuthenticated ? (
              <>
                <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
                <NavLink to="/notifications" icon={Bell} badge={notifUnreadCount}>Notifications</NavLink>
                <NavLink to="/favourites" icon={Heart}>Saved</NavLink>
                {isAdmin ? (
                  <NavLink to="/support" icon={Shield} badge={supportUnreadCount}>Support Inbox</NavLink>
                ) : (
                  <NavLink to="/support" icon={HelpCircle}>Help & Support</NavLink>
                )}
                <NavLink to="/create-listing" icon={Plus}>Post Listing</NavLink>

                {/* Notification prompt in mobile menu */}
                {notificationPermission === 'default' && (
                  <button
                    onClick={handleEnableNotifications}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#E53935] bg-red-50 hover:bg-red-100 transition-colors text-left text-sm"
                    data-testid="mobile-enable-notifications"
                  >
                    <Bell className="h-4 w-4" />
                    Enable message notifications
                  </button>
                )}

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-500 hover:bg-gray-50 transition-colors text-left text-sm font-medium"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 pt-2 border-t border-gray-100 mt-2">
                <Button 
                  variant="outline" 
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className="border-gray-200 text-gray-700"
                  data-testid="mobile-login-btn"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                  className="bg-primary hover:bg-[#097549] text-white"
                  data-testid="mobile-register-btn"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
